import { CipherType } from '../core-js/src/enums'
import { CipherData } from '../core-js/src/models/data'
import { Cipher } from '../core-js/src/models/domain'
import { Utils } from '../core-js/src/misc/utils'
import { ConstantsService } from '../core-js/src/services'

import global from '../config/global'

function convert_origin_item_to_cipher(item) {
  const cipherData = {
    id: item.id,
    name: item.name || null,
    organizationId: item.project_id || null,
    creationDate: item.creation_date || null,
    updatedDate: item.updated_date || null,
    revisionDate: item.revision_date || null,
    deletedDate: item.deleted_date || null,
    type: item.type
  }
  if (item.type === CipherType.Secret) {
    cipherData['secret'] = item.data
    cipherData['environmentId'] = item.environment_id
  } else if (item.type === CipherType.Environment) {
    cipherData['environment'] = {
      name: item.data.name,
      description: item.data.description,
      externalUrl: item.data.external_url
    }
  } else if (item.type === CipherType.LeakedSecret) {
    cipherData['leakedSecret'] = {
      key: item.data.key,
      value: item.data.value,
      description: item.data.description,
      location: item.data.location,
      lineInCode: item.data.line_in_code,
      imageUrl: item.data.image_url,
      commit: item.data.commit,
    }
  }
  return cipherData
}

async function clear_keys() {
  await global.jsCore.cryptoService.clearKeys()
}

async function make_key(
  username,
  password,
  kdf = null,
  kdfIterations = null,
  kdfMemory = undefined,
  kdfParallelism = undefined
) {
  if (global.jsCore) {
    return await global.jsCore.cryptoService.makeKey(
      password,
      username,
      kdf,
      kdfIterations,
      kdfMemory,
      kdfParallelism
    )
  }
  return ''
}

async function login_payload(data = {
  username,
  password,
  kdf,
  kdf_iterations,
  kdf_memory,
  kdf_parallelism,
}) {
  const makeKey = await make_key(
    data.username,
    data.password,
    data.kdf,
    data.kdf_iterations,
    data.kdf_memory,
    data.kdf_parallelism
  )
  const hashedPassword = await global.jsCore.cryptoService.hashPassword(data.password, makeKey)
  return {
    username: data.username,
    password: hashedPassword,
    kdf: data.kdf,
    kdf_iterations: data.kdf_iterations,
    kdf_memory: data.kdf_memory,
    kdf_parallelism: data.kdf_parallelism,
  }
}

async function auth_payload(data = {
  username,
  password,
  confirm_password,
  kdf,
  kdf_iterations,
  kdf_memory,
  kdf_parallelism,
}, isRegister = true) {
  if (global.jsCore) {
    const makeKey = await make_key(
      data.username,
      data.password,
      data.kdf,
      data.kdf_iterations,
      data.kdf_memory,
      data.kdf_parallelism
    )
    const encKey = await global.jsCore.cryptoService.makeEncKey(makeKey)
    const keys = await global.jsCore.cryptoService.makeKeyPair(encKey[0])
    const hashedPassword = await global.jsCore.cryptoService.hashPassword(data.password, makeKey)
    let payload = {
      email: data.username,
      username: data.username,
      password: hashedPassword,
      key: encKey[1].encryptedString,
      public_key: keys[0],
      private_key: keys[1].encryptedString,
      kdf: data.kdf,
      kdf_iterations: data.kdf_iterations,
      kdf_memory: data.kdf_memory,
      kdf_parallelism: data.kdf_parallelism,
    }
    if (isRegister) {
      const hashedConfirmPassword = await global.jsCore.cryptoService.hashPassword(data.confirm_password, makeKey)
      payload = {
        ...payload,
        confirm_password: hashedConfirmPassword
      }
    }
    return payload
  }
  return null
}

async function unlock(data = {
  username,
  password,
  kdf,
  kdf_iterations,
  kdf_memory,
  kdf_parallelism,
  kdf_version
}) {
  if (global.jsCore) {
    await clear_keys()
    const makeKey = await make_key(
      data.username,
      data.password,
      data.kdf,
      data.kdf_iterations,
      data.kdf_memory,
      data.kdf_parallelism
    )
    const hashedPassword = await global.jsCore.cryptoService.hashPassword(data.password, makeKey)
    await global.jsCore.tokenService.setTokens(data.token, null)
    await global.jsCore.userService.setInformation(
      global.jsCore.tokenService.getUserId(),
      data.username,
      data.kdf,
      data.kdf_version,
      data.kdf_iterations,
      data.kdf_memory,
      data.kdf_parallelism
    )
    await global.jsCore.cryptoService.setKey(makeKey)
    await global.jsCore.cryptoService.setKeyHash(hashedPassword)
    await global.jsCore.cryptoService.setEncKey(data.key)
    await global.jsCore.cryptoService.setEncPrivateKey(data.private_key)
    await global.jsCore.storageService.save(ConstantsService.lastActiveKey, new Date().getTime())
  }
}

async function lock() {
  if (global.jsCore) {
    await Promise.all([
      global.jsCore.cryptoService.clearKey(),
      global.jsCore.cryptoService.clearOrgKeys(true),
      global.jsCore.cryptoService.clearKeyPair(true),
      global.jsCore.cryptoService.clearEncKey(true)
    ])

    global.jsCore.cipherService.clearCache()
    global.jsCore.searchService.clearIndex()
  }
}

async function logout() {
  if (global.jsCore) {
    await clear_keys()
    await global.jsCore.userService.clear()
  }
}

async function sync_response(response) {
  try {
    const { secrets, environments } = response
    await global.jsCore.syncService.setLastSync(new Date())
    const userId = await global.jsCore.userService.getUserId()
    const allCiphers = [
      ...secrets.map(s => ({ ...s, type: CipherType.Secret })),
      ...environments.map(s => ({ ...s, type: CipherType.Environment }))
    ].map(c => convert_origin_item_to_cipher(c))

    const decryptedCipherCache = global.jsCore.cipherService.decryptedCipherCache || []
    const deletedIds = []
    decryptedCipherCache.forEach(cipher => {
      if (allCiphers.findIndex(c => c.id === cipher.id) < 0) {
        deletedIds.push(cipher.id)
      }
    })
    await Promise.all(deletedIds.map(async id => await global.jsCore.cipherService.delete(id)))
    await global.jsCore.syncService.syncSomeCiphers(userId, allCiphers)
  } catch (error) {
    console.log(error)
  }
}

async function decrypt_ciphers(items, type) {
  const userId = await global.jsCore.userService.getUserId();
  const ciphers = items.map((i) => {
    const cipher = convert_origin_item_to_cipher({ ...i, type: type });
    return new CipherData(cipher, userId)
  });
  const promises = [];
  for (const index in ciphers) {
    const c = new Cipher(ciphers[index], false);
    promises.push(c.decrypt())
  }
  const decCiphers = await Promise.all(promises);
  for (let i = 0; i < decCiphers.length; i++) {
    decCiphers[i].expiredDate = items[i]?.expired_date || null;
    decCiphers[i].creationDate = items[i]?.creation_date || null;
    decCiphers[i].updatedDate = items[i]?.updated_date || null;
    decCiphers[i].revisionDate = items[i]?.revision_date || null;
  }
  return decCiphers
}

async function hash_key_with_project_id(key, projectId) {
  const projectIdArrayBuffer = global.jsCore.cryptoFunctionService.toBuf(`${projectId}`);
  const keyArrayBuffer = global.jsCore.cryptoFunctionService.toBuf(key);
  const arrayBufferData = new Uint8Array(projectIdArrayBuffer.byteLength + keyArrayBuffer.byteLength);
  arrayBufferData.set(new Uint8Array(projectIdArrayBuffer), 0);
  arrayBufferData.set(new Uint8Array(keyArrayBuffer), projectIdArrayBuffer.byteLength);
  const hashBytes = await global.jsCore.cryptoFunctionService.hash(arrayBufferData.buffer, 'sha256');
  return Utils.fromBufferToUrlB64(hashBytes)
}

export default {
  convert_origin_item_to_cipher,
  clear_keys,
  make_key,
  login_payload,
  auth_payload,
  unlock,
  lock,
  logout,
  sync_response,
  decrypt_ciphers,
  hash_key_with_project_id
}
